import { isEqual } from 'lodash';
import { rotatePointWithFocus } from '@/utils/math-kit';
import type { Cell, ITableLayer } from '@/version-types';
import { getTableCellRects } from '../../../utils/geometry';
import type { CellInfo, CellsSelectedInfo } from '../state';

type Point = { x: number; y: number };
type SimpleCellInfo = {
    id: string;
    hIndex: number;
    vIndex: number;
    colSpan: number;
    rowSpan: number;
    top: number;
    left: number;
    width: number;
    height: number;
};

/** 效果图: https://xiuxiu-pro-pre.meitudata.com/posters/63b0dc65be5cdcce341cf10365bdc155.png */
const getCellsByRange = ({
    allCells,
    range,
}: {
    allCells: SimpleCellInfo[];
    /** 相对于tableLayer内布局左上角的坐标 */
    range: { startPoint: Point; lastPoint: Point };
}): SimpleCellInfo[] | null => {
    const selectedCells = allCells.filter((cell) => {
        const cellStartX = cell.left;
        const cellStartY = cell.top;
        const cellEndX = cell.left + cell.width;
        const cellEndY = cell.top + cell.height;

        /** startPoint和lastPoint两个点组成的矩形 */
        const rectBounding = {
            top: Math.min(range.startPoint.y, range.lastPoint.y),
            left: Math.min(range.startPoint.x, range.lastPoint.x),
            right: Math.max(range.startPoint.x, range.lastPoint.x),
            bottom: Math.max(range.startPoint.y, range.lastPoint.y),
        };

        // Check if any part of the box intersects with the line
        if (
            cellStartX < rectBounding.right &&
            cellEndX > rectBounding.left &&
            cellStartY < rectBounding.bottom &&
            cellEndY > rectBounding.top
        ) {
            return true;
        }

        return false;
    });

    return selectedCells.length ? selectedCells : null;
};

type RectBounding = {
    top: number;
    left: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
};

/** 根据cells, 获取最左、最右、最上、最下的值 */
const getBoxesRectBounding = <T extends { top: number; left: number; width: number; height: number }>(
    boxes: T[],
): RectBounding => {
    const getBoundingItem = (boxes: T[], mode: 'top' | 'left' | 'right' | 'bottom') =>
        boxes.reduce((n, box) => {
            const { top, left, width, height } = box;
            const right = box.left + width;
            const bottom = box.top + height;
            // initial value
            if (isNaN(n)) {
                if (mode === 'top') return top;
                if (mode === 'bottom') return bottom;
                if (mode === 'left') return left;
                return right;
            }
            if (mode === 'top') return top < n ? top : n;
            if (mode === 'bottom') return bottom > n ? bottom : n;
            if (mode === 'left') return left < n ? left : n;
            return right > n ? right : n;
        }, NaN);
    const top = getBoundingItem(boxes, 'top');
    const left = getBoundingItem(boxes, 'left');
    const right = getBoundingItem(boxes, 'right');
    const bottom = getBoundingItem(boxes, 'bottom');
    const rectBounding: RectBounding = {
        top,
        left,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
    };

    return rectBounding;
};

/**
 * 单元格多选逻辑
 * 将框选到的cells补充成一个矩形, 并返回最终框选的cells和矩形信息
 * 如补全这个效果图: https://xiuxiu-pro-pre.meitudata.com/posters/63b0dc65be5cdcce341cf10365bdc155.png
 * 结果为: https://xiuxiu-pro-pre.meitudata.com/posters/27b271b2da6d1f05c5439a2b02ba37cb.png
 * 如补全这个效果图: https://xiuxiu-pro-pre.meitudata.com/posters/17c86dd87ca72248e091762050a65fbb.png
 * 结果为: https://xiuxiu-pro-pre.meitudata.com/posters/330cf7329508f59d3ea6d80553e364f7.png
 */
export const getCellsSelectedInfoByMouseRange = ({
    tableLayer,
    range,
}: {
    tableLayer: ITableLayer;
    /** 鼠标滑动范围, 坐标: 相对于画布左上角 */
    range: Record<'startCoords' | 'lastCoords', { stageX: number; stageY: number }>;
}): CellsSelectedInfo | null => {
    const { cells: cellsProp } = tableLayer.data;

    /** 相对于tableLayer内布局左上角的坐标 */
    const tableCenterPoint = {
        x: tableLayer.left + tableLayer.width / 2,
        y: tableLayer.top + tableLayer.height / 2,
    };

    /** 旋转前, 相对于tableLayer内布局左上角的坐标 */
    const fakeStartPoint = rotatePointWithFocus(
        {
            x: range.startCoords.stageX,
            y: range.startCoords.stageY,
        },
        tableCenterPoint,
        -tableLayer.rotate,
    );

    /** 旋转前, 相对于tableLayer内布局左上角的坐标 */
    const fakeEndPoint = rotatePointWithFocus(
        {
            x: range.lastCoords.stageX,
            y: range.lastCoords.stageY,
        },
        tableCenterPoint,
        -tableLayer.rotate,
    );

    /** 相对于tableLayer内布局左上角的坐标 */
    const startPoint = {
        x: fakeStartPoint.x - tableLayer.left,
        y: fakeStartPoint.y - tableLayer.top,
    };
    /** 相对于tableLayer内布局左上角的坐标 */
    const lastPoint = {
        x: fakeEndPoint.x - tableLayer.left,
        y: fakeEndPoint.y - tableLayer.top,
    };

    const cells = cellsProp.flat().filter(Boolean) as Cell[];
    const allCells: SimpleCellInfo[] = getTableCellRects(tableLayer).map((rect) => ({
        id: rect.id,
        hIndex: rect.hIndex,
        vIndex: rect.vIndex,
        colSpan: rect.cell.colSpan,
        rowSpan: rect.cell.rowSpan,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
    }));

    const rectBounding = (function getRectBounding(_oldSelectedCells: SimpleCellInfo[] | null) {
        let oldSelectedCells: SimpleCellInfo[];
        if (_oldSelectedCells) {
            oldSelectedCells = _oldSelectedCells;
        } else {
            const initialSelectedCells = getCellsByRange({
                allCells,
                range: {
                    startPoint,
                    lastPoint,
                },
            });
            if (!initialSelectedCells) return null;
            oldSelectedCells = initialSelectedCells;
        }

        const rectBounding = getBoxesRectBounding(oldSelectedCells);
        const newSelectedCells = getCellsByRange({
            allCells,
            range: {
                // 给个0.001的偏差, 防止js精度问题导致计算出错
                startPoint: {
                    x: rectBounding.left + 0.001,
                    y: rectBounding.top + 0.001,
                },
                lastPoint: {
                    x: rectBounding.right - 0.001,
                    y: rectBounding.bottom - 0.001,
                },
            },
        });
        const isEqualRectSelectedCells = (aCells: SimpleCellInfo[], bCells: SimpleCellInfo[]) => {
            const aCellIds = aCells.map((cell) => cell.id).sort((a, b) => a.localeCompare(b));
            const bCellIds = bCells.map((cell) => cell.id).sort((a, b) => a.localeCompare(b));
            return isEqual(aCellIds, bCellIds);
        };
        if (newSelectedCells && isEqualRectSelectedCells(oldSelectedCells, newSelectedCells)) {
            return rectBounding;
        }
        return getRectBounding(newSelectedCells);
    })(null);

    if (!rectBounding) return null;

    const selectedCellInfos = allCells.filter((cell) => {
        const { top, left, width, height } = cell;
        const right = cell.left + width;
        const bottom = cell.top + height;
        return (
            top >= rectBounding.top &&
            left >= rectBounding.left &&
            right <= rectBounding.right &&
            bottom <= rectBounding.bottom
        );
    });
    if (!selectedCellInfos.length) return null;

    const rect: CellsSelectedInfo['rect'] = {
        startHIndex: Math.min(...selectedCellInfos.map((cell) => cell.hIndex)),
        startVIndex: Math.min(...selectedCellInfos.map((cell) => cell.vIndex)),
        endHIndex: Math.max(...selectedCellInfos.map((cell) => cell.hIndex + cell.colSpan)),
        endVIndex: Math.max(...selectedCellInfos.map((cell) => cell.vIndex + cell.rowSpan)),
    };

    const targetCellIds = selectedCellInfos.map((c) => c.id);

    return {
        rect,
        cellInfoList: cells
            .filter((c) => targetCellIds.includes(c.__renderId))
            .map((c) => ({
                id: c.__renderId,
                hIndex: c.hIndex,
                vIndex: c.vIndex,
            })),
    };
};

export const appendCellsSelectedInfoByCellInfo = ({
    tableLayer,
    originalRect,
    targetCellInfo,
}: {
    tableLayer: ITableLayer;
    originalRect: CellsSelectedInfo['rect'];
    targetCellInfo: CellInfo;
}): CellsSelectedInfo | null => {
    const targetCell = tableLayer.data.cells[targetCellInfo.vIndex]?.[targetCellInfo.hIndex];
    if (!targetCell) return null;

    const getSelectedCells = (rect: CellsSelectedInfo['rect']): Cell[] => {
        const cells = tableLayer.data.cells.flat().filter(Boolean) as Cell[];
        return cells.filter(
            (cell) =>
                cell.hIndex < rect.endHIndex &&
                cell.hIndex + cell.colSpan > rect.startHIndex &&
                cell.vIndex < rect.endVIndex &&
                cell.vIndex + cell.rowSpan > rect.startVIndex,
        );
    };

    const expandRect = (rect: CellsSelectedInfo['rect']): CellsSelectedInfo['rect'] | null => {
        const selectedCells = getSelectedCells(rect);
        if (!selectedCells.length) return null;

        const nextRect: CellsSelectedInfo['rect'] = {
            startHIndex: Math.min(...selectedCells.map((cell) => cell.hIndex)),
            startVIndex: Math.min(...selectedCells.map((cell) => cell.vIndex)),
            endHIndex: Math.max(...selectedCells.map((cell) => cell.hIndex + cell.colSpan)),
            endVIndex: Math.max(...selectedCells.map((cell) => cell.vIndex + cell.rowSpan)),
        };

        if (isEqual(rect, nextRect)) return rect;
        return expandRect(nextRect);
    };

    const rect = expandRect({
        startHIndex: Math.min(originalRect.startHIndex, targetCell.hIndex),
        startVIndex: Math.min(originalRect.startVIndex, targetCell.vIndex),
        endHIndex: Math.max(originalRect.endHIndex, targetCell.hIndex + targetCell.colSpan),
        endVIndex: Math.max(originalRect.endVIndex, targetCell.vIndex + targetCell.rowSpan),
    });
    if (!rect) return null;

    return {
        rect,
        cellInfoList: getSelectedCells(rect).map((cell) => ({
            id: cell.__renderId,
            hIndex: cell.hIndex,
            vIndex: cell.vIndex,
        })),
    };
};
